// @givmo/api-client — the hand-modeled error envelope + Givmo error hierarchy.
//
// The backend error body is `{ "errors": [{ code, title, detail, request_id }] }`.
// We HAND-MODEL it here and ignore the generator's `HTTPValidationError` (which
// is not even present in the current snapshot) — the generated error unions are
// per-operation and shape-incomplete for envelope handling. Ports:
//   - givmo-payment-repository `error_handler.dart` (`errorHandler` + the
//     triple-source `_extractRequestId`) and `failure_model.dart` (`Failure`,
//     `InternetConnectionFailure`, `ServerFailure` + the PII-safe `toString`),
//   - the `_persistentRequestId` transient-suppression predicate (webapp
//     reports/auth/settings cubits) and the `ErrorReferenceText` `(ref: …)`
//     support string.
//
// PII rule (the split): `message` is the user-facing copy the UI renders — it
// carries the backend's `errors[0].detail` (falling back to `errors[0].title`,
// then a generic default) so the user sees the same server copy the Flutter app
// showed. `toString()` and the network logger NEVER widen: they emit only the
// class, kind, and status — never `detail`/`title` (PII-adjacent) and never the
// `requestId`. So server copy reaches the screen; logs stay generic. This ports
// the Dart split — `ServerError.fromJson` reads `errors[0]['detail']` and
// `errorHandler` returns it as the user message (generic default only when
// none is present), while `Failure.toString()` emitted only the error code.

/** One entry of the backend error envelope. All fields are tolerated-optional. */
export interface GivmoErrorEntry {
  /** Numeric error code (backend convention). */
  readonly code?: number;
  /** Short server title (may be localized; treated as PII-adjacent — not logged). */
  readonly title?: string;
  /** Server-derived detail (may embed PII — never logged, never put in toString). */
  readonly detail?: string;
  /** Correlation id echoed by the backend. */
  readonly requestId?: string;
  /**
   * The stable machine-readable `error_code` string the backend adds ADDITIVELY to an envelope
   * entry (e.g. `CONSENT_UNAVAILABLE`, `GUEST_EMAIL_REGISTERED`, `PLEDGE_ESTABLISHMENT_FAILED`,
   * `INVALID_PLEDGE_CLAIM_TOKEN`). A fixed-vocabulary token, so — unlike `detail`/`title` — it is
   * PII-safe: the backend added it precisely so clients branch on a code instead of parsing the
   * English detail. Absent on entries that carry no code. Read it via {@link firstErrorCode}.
   */
  readonly errorCode?: string;
  /**
   * The optional structured `context` bag a small number of handlers attach to an envelope entry
   * ADDITIVELY (e.g. the company-admin batch handler's `{invalid_recipients:[…]}`). Free-form JSON
   * keyed off the specific error, so a consumer that recognises the `errorCode` can pull the exact
   * offending rows instead of parsing English. Treated PII-adjacent (may embed user data such as a
   * work email) exactly like `detail`: captured for the UI but NEVER logged — `toString()`/the
   * network logger never touch `entries`, so this stays off logs. Tolerated-optional; absent on
   * entries without one.
   */
  readonly context?: Record<string, unknown>;
}

/** Discriminates the error variants without leaking which is which to logs. */
export type GivmoErrorKind = 'network' | 'server' | 'request' | 'unknown';

/**
 * The single, machine-readable `error.code` string off the Connect partner
 * envelope (`{error:{type,code,message,…}}`) — the ONLY branch key a consumer
 * should switch on (e.g. `intent_already_completed`). `undefined` for the legacy
 * `{errors:[…]}` surface (whose per-entry `code` is a NUMBER on {@link GivmoErrorEntry}).
 * Treated as PII-safe: it is a fixed vocabulary token, so — unlike `message` —
 * the network logger MAY emit it (see {@link GivmoError.toString}).
 */
export type ConnectErrorCode = string;

/**
 * The canonical client error. Construct via {@link mapResponseError} /
 * {@link mapTransportError}; do not throw raw fetch errors past the boundary.
 */
export class GivmoError extends Error {
  /**
   * User-facing copy, safe to RENDER but never to LOG. When the error body carried one, this is the
   * SERVER's own text — `errors[0].detail` (or `.title`), or the Connect envelope's `message` — and
   * only falls back to a generic, kind-appropriate default when the body supplied none (see
   * {@link mapResponseError}). Consumers deliberately surface it verbatim rather than forking copy.
   *
   * Because it can be server-derived it is PII-adjacent: it is excluded from {@link toString} and
   * from the network logger. Do not branch on its text — use `status`, or the fixed-vocabulary
   * {@link firstErrorCode} / {@link connectCode} tokens.
   */
  override readonly message: string;
  readonly kind: GivmoErrorKind;
  /** HTTP status when a response was produced; undefined on transport failure. */
  readonly status?: number;
  /** Parsed envelope entries, if the body matched the envelope shape. */
  readonly entries: ReadonlyArray<GivmoErrorEntry>;
  /** Correlation id, resolved by the triple-source extractor. */
  readonly requestId?: string;
  /**
   * The Connect envelope's string `error.code`, when the body matched the
   * `{error:{…}}` shape (undefined for legacy `{errors:[…]}`). This is the
   * stable branch key for Connect flows — e.g. the sheet treats a `409`
   * `intent_already_completed` as terminal-success. PII-safe (fixed token).
   */
  readonly connectCode?: ConnectErrorCode;
  /**
   * The Connect envelope's `error.type` (`conflict`/`authentication`/… — the
   * coarse class), when the body matched `{error:{…}}`. Advisory only; branch on
   * {@link connectCode}, not this. Undefined for legacy `{errors:[…]}`.
   */
  readonly connectType?: string;

  constructor(args: {
    message: string;
    kind: GivmoErrorKind;
    status?: number;
    entries?: ReadonlyArray<GivmoErrorEntry>;
    requestId?: string;
    connectCode?: ConnectErrorCode;
    connectType?: string;
  }) {
    super(args.message);
    this.name = 'GivmoError';
    this.message = args.message;
    this.kind = args.kind;
    this.status = args.status;
    this.entries = args.entries ?? [];
    this.requestId = args.requestId;
    this.connectCode = args.connectCode;
    this.connectType = args.connectType;
  }

  /** True for retryable transport failures (no response): timeout / offline / abort. */
  get isTransient(): boolean {
    return this.kind === 'network';
  }

  /**
   * PII-safe stringification. Ports `Failure.toString()` — emits ONLY the class,
   * kind, status, and (when present) the Connect `error.code`. NO server-derived
   * `detail`/`title`/`message` and NO `requestId` (kept off `toString` to match
   * the Flutter original's compact log lines). The Connect `code` is a fixed
   * vocabulary token (never free-form / PII), so surfacing it aids diagnosis
   * without widening the PII surface. `toString` is hit implicitly by template
   * interpolation, `console.*`, and crash reporters, so it must never widen
   * beyond these fixed, non-PII fields.
   */
  override toString(): string {
    const status = this.status ?? 'no-response';
    const code = this.connectCode ? `, code: ${this.connectCode}` : '';
    return `GivmoError(kind: ${this.kind}, status: ${status}${code})`;
  }
}

const DEFAULT_MESSAGE =
  'Sorry, we cannot process your request at the moment. Please contact the support team.';
const SERVER_MESSAGE = 'Our servers had trouble responding. Please try again in a moment.';
const OFFLINE_MESSAGE = 'You appear to be offline. Check your internet connection and try again.';
const LIMITED_USE_APP_CHECK_MESSAGE =
  'Security attestation failed for this action. Try again; if it persists, check browser extensions or network filtering.';
const REAUTH_REPLAY_MESSAGE =
  'You confirmed it’s you, but no response came back when the action was resent. Check whether it went through before trying again.';

/** Internal marker for a limited-use App Check token that could not be minted fail-closed. */
export class LimitedUseAppCheckError extends Error {
  constructor() {
    super('Limited-use App Check token mint failed.');
    this.name = 'LimitedUseAppCheckError';
  }
}

/**
 * Internal marker for a guarded money action whose one-shot replay produced NO RESPONSE after the
 * operator re-authenticated. Distinct from {@link LimitedUseAppCheckError} (fresh proof could not be
 * minted) and from the original `reauth_required` 401, which must never be re-surfaced once identity
 * is proven — that reads as "authenticate again" when the resend is what failed.
 */
export class ReauthReplayError extends Error {
  constructor() {
    super('Post-reauthentication replay produced no response.');
    this.name = 'ReauthReplayError';
  }
}

/**
 * Parse the `{ errors: [...] }` envelope off an already-decoded error body.
 * Returns `[]` when the body does not match the envelope shape — never throws.
 */
export function parseErrorEnvelope(body: unknown): ReadonlyArray<GivmoErrorEntry> {
  if (typeof body !== 'object' || body === null) return [];
  const errors = (body as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return [];
  const entries: GivmoErrorEntry[] = [];
  for (const raw of errors) {
    if (typeof raw !== 'object' || raw === null) continue;
    const e = raw as Record<string, unknown>;
    entries.push({
      code: typeof e.code === 'number' ? e.code : undefined,
      title: typeof e.title === 'string' ? e.title : undefined,
      detail: typeof e.detail === 'string' ? e.detail : undefined,
      requestId: typeof e.request_id === 'string' ? e.request_id : undefined,
      // ADDITIVE: the backend's machine-readable `error_code` (PII-safe fixed vocabulary), captured
      // only when the entry carries one. Tolerated-optional — absent on entries without a code.
      errorCode:
        typeof e.error_code === 'string' && e.error_code.length > 0 ? e.error_code : undefined,
      // ADDITIVE: the structured `context` bag (e.g. batch `invalid_recipients`), captured only when
      // it is a plain object. PII-adjacent — surfaced to the UI, never logged (see the doc above).
      context:
        typeof e.context === 'object' && e.context !== null && !Array.isArray(e.context)
          ? (e.context as Record<string, unknown>)
          : undefined,
    });
  }
  return entries;
}

/**
 * The Connect partner error envelope, decoded off an already-parsed body — the
 * single-object `{ error: { type, code, message, param?, doc_url?, request_id? } }`
 * shape every `/connect/*` route emits (deliberately different from the legacy
 * consumer `{ errors: [...] }` array). All fields are tolerated-optional so a
 * partial/mistyped body degrades gracefully rather than throwing.
 *
 * `message` is server-derived (may embed PII — never logged, same rule as the
 * legacy `detail`); `code`/`type` are fixed vocabulary tokens (PII-safe).
 */
export interface ConnectErrorEnvelope {
  /** Coarse class — `conflict`/`authentication`/`rate_limit`/… (fixed vocabulary). */
  readonly type?: string;
  /** Stable machine-readable branch key — e.g. `intent_already_completed`. */
  readonly code?: string;
  /** Server-derived user-facing copy (PII-adjacent — never logged / never in toString). */
  readonly message?: string;
  /** Correlation id echoed by the backend (inside `error`, not a top-level key). */
  readonly requestId?: string;
}

/**
 * Recognize + parse the Connect `{ error: {…} }` partner envelope off an
 * already-decoded error body. Returns `null` when the body is not that shape
 * (missing/blank a non-object `error`, or `error` lacks a string `code`) so the
 * caller can FALL THROUGH to the legacy `{ errors: [...] }` path — this is the
 * additive detector that must never mistake a legacy body for a Connect one.
 * Never throws.
 *
 * Detection is deliberately strict: `error` must be a non-null object AND carry
 * a non-empty string `code`. The legacy envelope has a top-level `errors` ARRAY
 * (not an `error` object), so the two shapes never collide.
 */
export function parseConnectErrorEnvelope(body: unknown): ConnectErrorEnvelope | null {
  if (typeof body !== 'object' || body === null) return null;
  const error = (body as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null || Array.isArray(error)) return null;
  const e = error as Record<string, unknown>;
  const code = typeof e.code === 'string' && e.code.length > 0 ? e.code : undefined;
  // A Connect envelope is identified by a string `code` on the `error` object;
  // without it we cannot distinguish it from an arbitrary body, so fall through.
  if (code === undefined) return null;
  return {
    type: typeof e.type === 'string' ? e.type : undefined,
    code,
    message: typeof e.message === 'string' && e.message.length > 0 ? e.message : undefined,
    requestId:
      typeof e.request_id === 'string' && e.request_id.length > 0 ? e.request_id : undefined,
  };
}

/**
 * Triple-source correlation-id extractor — ports `_extractRequestId`.
 * Precedence: (1) the client-stamped id we put on the request via
 * {@link REQUEST_ID_HEADER} (survives transport errors with no response) →
 * (2) the `x-request-id` response header (browser-readable only when CORS
 * exposes it) → (3) `errors[0].request_id` from the envelope (CORS-independent).
 * Empty strings fall through to the next source.
 */
export function extractRequestId(args: {
  stamped?: string | null;
  responseHeader?: string | null;
  entries?: ReadonlyArray<GivmoErrorEntry>;
}): string | undefined {
  const stamped = args.stamped;
  if (typeof stamped === 'string' && stamped.length > 0) return stamped;

  const header = args.responseHeader;
  if (typeof header === 'string' && header.length > 0) return header;

  const fromEnvelope = args.entries?.[0]?.requestId;
  if (typeof fromEnvelope === 'string' && fromEnvelope.length > 0) return fromEnvelope;

  return undefined;
}

/**
 * Pick the user-facing message off the parsed envelope — ports the Dart
 * `ServerError.fromJson` (`errorMessage = errors[0]['detail']`) plus the
 * `errorHandler` fallback. Prefers `errors[0].detail`, then `errors[0].title`,
 * else `null` so the caller substitutes its kind-appropriate generic default.
 *
 * This is the ONLY place server-derived copy crosses into `message`. It must
 * never reach `toString()`/the logger — that PII-safe split is enforced in
 * {@link GivmoError.toString} and the `NetworkLogMeta` type.
 */
function serverMessage(entries: ReadonlyArray<GivmoErrorEntry>): string | null {
  const first = entries[0];
  if (!first) return null;
  if (typeof first.detail === 'string' && first.detail.length > 0) return first.detail;
  if (typeof first.title === 'string' && first.title.length > 0) return first.title;
  return null;
}

/**
 * Map a non-2xx HTTP response into a {@link GivmoError}. `body` is the decoded
 * error body (object or string). 5xx → `server`, other 4xx/non-network → `request`.
 *
 * `message` carries the backend's `errors[0].detail` (or `.title`) when present
 * — the same server copy the Flutter app surfaced — falling back to a generic,
 * kind-appropriate default only when the envelope supplies none. The server
 * detail NEVER reaches `toString()`/the logger (see {@link GivmoError.toString}).
 *
 * ADDITIVE Connect support: when the body is the Connect partner envelope
 * (`{error:{…}}`), its `message` becomes the user copy and its string `code`
 * is surfaced on {@link GivmoError.connectCode}. This is detected FIRST and,
 * when it does not match, the function falls through UNCHANGED to the legacy
 * `{errors:[…]}` path — so existing consumers are byte-for-byte unaffected.
 */
export function mapResponseError(args: {
  status: number;
  body: unknown;
  stampedRequestId?: string | null;
  responseHeader?: string | null;
}): GivmoError {
  // Connect envelope FIRST (additive): a `{error:{…}}` body with a string `code`.
  // Non-matching bodies return null and fall through to the legacy path below.
  const connect = parseConnectErrorEnvelope(args.body);
  if (connect !== null) {
    const requestId = extractRequestId({
      stamped: args.stampedRequestId,
      responseHeader: args.responseHeader,
      // The Connect request_id lives inside `error`; surface it via the same
      // extractor by shimming it into the entries slot's requestId source.
      entries: connect.requestId ? [{ requestId: connect.requestId }] : [],
    });
    const kind: GivmoErrorKind = args.status >= 500 ? 'server' : 'request';
    return new GivmoError({
      message: connect.message ?? (kind === 'server' ? SERVER_MESSAGE : DEFAULT_MESSAGE),
      kind,
      status: args.status,
      requestId,
      connectCode: connect.code,
      connectType: connect.type,
    });
  }

  const entries = parseErrorEnvelope(args.body);
  const requestId = extractRequestId({
    stamped: args.stampedRequestId,
    responseHeader: args.responseHeader,
    entries,
  });
  const fromServer = serverMessage(entries);
  if (args.status >= 500) {
    return new GivmoError({
      message: fromServer ?? SERVER_MESSAGE,
      kind: 'server',
      status: args.status,
      entries,
      requestId,
    });
  }
  return new GivmoError({
    message: fromServer ?? DEFAULT_MESSAGE,
    kind: 'request',
    status: args.status,
    entries,
    requestId,
  });
}

/**
 * Map a transport failure (no response produced — timeout, offline, DNS, abort)
 * into a transient {@link GivmoError}. Mirrors the Dio `_networkDioExceptionTypes`
 * → `InternetConnectionFailure` branch.
 */
export function mapTransportError(args: { stampedRequestId?: string | null }): GivmoError {
  return new GivmoError({
    message: OFFLINE_MESSAGE,
    kind: 'network',
    requestId: args.stampedRequestId ?? undefined,
  });
}

/** Map a fail-closed limited-use App Check mint failure without mislabeling it as offline. */
export function mapLimitedUseAppCheckError(args: { stampedRequestId?: string | null }): GivmoError {
  return new GivmoError({
    message: LIMITED_USE_APP_CHECK_MESSAGE,
    kind: 'request',
    requestId: args.stampedRequestId ?? undefined,
  });
}

/**
 * Map a post-reauthentication replay that produced no response. The operator proved identity and the
 * replay was the action's FIRST execution attempt (the `reauth_required` refusal is raised before the
 * handler, with no side effect) — but a rejected fetch is NOT proof the server never received it: a
 * connection dropped after the body left the browser rejects exactly like one that was refused, and
 * not every guarded money route is idempotent. So the copy says what is known (no response), tells
 * the operator to check the action's state before retrying, and never claims nothing was performed.
 * Transient like any transport failure (the cure is still to try again, once checked); never the
 * offline line, never the stale "re-authentication required".
 */
export function mapReauthReplayError(args: { stampedRequestId?: string | null }): GivmoError {
  return new GivmoError({
    message: REAUTH_REPLAY_MESSAGE,
    kind: 'network',
    requestId: args.stampedRequestId ?? undefined,
  });
}

/**
 * The support-quotable `(ref: …)` string with transient-failure suppression —
 * ports the `_persistentRequestId` predicate + `ErrorReferenceText`.
 *
 * Transient (network) failures usually resolve on retry, so surfacing a ref id
 * there is noise without payoff; only PERSISTENT failures (5xx, 4xx,
 * non-network) carry the ref. Returns `null` when there is nothing to show.
 */
export function supportReference(error: GivmoError): string | null {
  if (error.isTransient) return null;
  const id = error.requestId;
  if (!id) return null;
  return `ref: ${id}`;
}

/** Narrow an unknown value (e.g. an error returned in the `error` field) to a `GivmoError`. */
export function isGivmoError(value: unknown): value is GivmoError {
  return value instanceof GivmoError;
}

/**
 * The stable machine-readable `error_code` off the first envelope entry, or `null` when none is
 * present. This is the PII-safe branch key the backend adds additively (e.g. `CONSENT_UNAVAILABLE`)
 * so a consumer can map a specific failure to tailored copy WITHOUT parsing the English `message` —
 * the same signal iOS branches on. Prefers the legacy `{errors:[…]}` entry code; falls back to the
 * Connect envelope's string `code` so one accessor covers both error shapes.
 */
export function firstErrorCode(error: GivmoError): string | null {
  const fromEnvelope = error.entries[0]?.errorCode;
  if (typeof fromEnvelope === 'string' && fromEnvelope.length > 0) return fromEnvelope;
  if (typeof error.connectCode === 'string' && error.connectCode.length > 0)
    return error.connectCode;
  return null;
}

/**
 * The structured `context` bag off the first envelope entry, or `null` when none is present. Pairs
 * with {@link firstErrorCode}: the backend attaches `context` to specific `error_code`s (e.g.
 * `COMPANY_DOMAIN_JOINABLE` → `{ resolution, company: { company_id, name, logo_url } }`), so a
 * consumer branches on the code and then reads the fields it expects off this bag. The values are
 * `unknown` — the caller narrows them (tolerant reader); never assume a field is present.
 */
export function firstErrorContext(error: GivmoError): Record<string, unknown> | null {
  return error.entries[0]?.context ?? null;
}
